import type { CopilotSuggestion, SuggestionType } from '../api/mediatorService';
import type { Party } from '../types/case';
import { humanizeSpeakerRefs } from '../lib/speakers';

interface Props {
  suggestion: CopilotSuggestion;
  parties: Party[];
  onDismiss: (id: string) => void;
  onPin: (id: string) => void;
}

const ICON: Record<SuggestionType, string> = {
  fact_check: '🔍',
  policy: '⚖️',
  math: '🔢',
  strategy: '💡',
  key_moment: '📌',
  agreement_term: '📝',
};

const TYPE_LABEL: Record<SuggestionType, string> = {
  fact_check: 'Fact check',
  policy: 'Policy',
  math: 'Math check',
  strategy: 'Strategy',
  key_moment: 'Key moment',
  agreement_term: 'Agreement term',
};

const priorityBorder = (priority: CopilotSuggestion['priority']): string => {
  switch (priority) {
    case 'high':
      return 'border-l-4 border-l-red-500';
    case 'medium':
      return 'border-l-4 border-l-amber-400';
    default:
      return 'border-l-4 border-l-gray-300';
  }
};

export function SuggestionCard({ suggestion, parties, onDismiss, onPin }: Props) {
  const pinned = suggestion.status === 'pinned';
  const reference = suggestion.reference
    ? humanizeSpeakerRefs(suggestion.reference, parties)
    : null;

  return (
    <div
      className={[
        'card p-3 animate-suggestion-in',
        priorityBorder(suggestion.priority),
        suggestion.priority === 'high' ? 'animate-pulse-once' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
          <span aria-hidden>{ICON[suggestion.type]}</span>
          <span className="uppercase tracking-wider">{TYPE_LABEL[suggestion.type]}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            onClick={() => onPin(suggestion.id)}
            title={pinned ? 'Pinned' : 'Pin'}
          >
            {pinned ? '★' : '☆'}
          </button>
          <button
            type="button"
            className="rounded px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            onClick={() => onDismiss(suggestion.id)}
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      </div>
      <p className="mt-1.5 text-sm leading-relaxed text-gray-900">{suggestion.content}</p>
      {reference && (
        <p className="mt-1 text-xs italic text-gray-500">“{reference}”</p>
      )}
    </div>
  );
}
